/*#
# Files: what a task can carry

A task can carry files as well as links (board #2436): drop one on a task
on the board, or `virta attach #n ./shot.png` from a shell. The bytes go to
the host's private `virta:files` storage area (service-compris 0.3.0) and
the task gets an `attached` event naming them. Readers fetch a file with
their own credential, through `/blob`; nobody else can read it.

- **What:** images, video, audio, PDF, markdown and plain text, up to
  25 MB each. Anything else is refused **before** it's sent.
- **Where:** a file's path is its content hash (`<sha256>.<ext>`), so the
  same bytes are one file however many tasks carry them.
- **Links stay links:** a URL is attached as a link, never copied.

```js
import { contentTypeFor, fileRefusal, filePathFor } from 'tosijs-virta'

contentTypeFor('shot.png')          // 'image/png'
fileRefusal('notes.exe', 1024)      // why it can't be attached
filePathFor('ab12…', 'shot.png')    // 'ab12….png'
```
*/

/*{ "parent": "Reference", "order": 36 }*/

import {
  FILES_AREA_NAME,
  FILES_CONTENT_TYPES,
  FILES_MAX_BYTES,
} from './manifest'

/** Content types by extension, for the kinds of file a task carries. */
const TYPES: Readonly<Record<string, string>> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  avif: 'image/avif',
  svg: 'image/svg+xml',
  mp4: 'video/mp4',
  webm: 'video/webm',
  mov: 'video/quicktime',
  m4v: 'video/mp4',
  mp3: 'audio/mpeg',
  m4a: 'audio/mp4',
  ogg: 'audio/ogg',
  oga: 'audio/ogg',
  wav: 'audio/wav',
  flac: 'audio/flac',
  pdf: 'application/pdf',
  md: 'text/markdown',
  markdown: 'text/markdown',
  txt: 'text/plain',
  log: 'text/plain',
}

const extensionOf = (name: string) =>
  (/\.([A-Za-z0-9]+)$/.exec(name)?.[1] ?? '').toLowerCase()

/** A file's content type from its name, or '' when it isn't a kind a task carries. */
export function contentTypeFor(name: string, given = ''): string {
  // what the browser says, when it's one of ours; else the extension
  const type = given.split(';')[0]!.trim().toLowerCase()
  if (type && typeAllowed(type)) return type
  const ext = extensionOf(name)
  return Object.hasOwn(TYPES, ext) ? TYPES[ext]! : ''
}

/** Whether the files area takes this content type (`image/*` covers `image/png`). */
export function typeAllowed(type: string): boolean {
  return FILES_CONTENT_TYPES.some((allowed) =>
    allowed.endsWith('/*')
      ? type.startsWith(allowed.slice(0, -1))
      : type === allowed
  )
}

/**
 * Why a file can't be attached, or '' when it can — decided before any
 * bytes move, so a refused file costs nothing to send.
 */
export function fileRefusal(name: string, bytes: number, given = ''): string {
  if (!contentTypeFor(name, given))
    return `${name}: not a kind of file a task carries (images, video, audio, PDF, markdown, text)`
  if (bytes > FILES_MAX_BYTES)
    return `${name} is ${(bytes / 1048576).toFixed(
      1
    )} MB; a file can be at most ${FILES_MAX_BYTES / 1048576} MB`
  return ''
}

/** A file's path in the area: its hash, and the extension its type implies. */
export function filePathFor(sha256: string, name: string): string {
  const ext = extensionOf(name).replace(/[^a-z0-9]/g, '')
  return ext ? `${sha256}.${ext}` : sha256
}

/** The SHA-256 of some bytes, as hex (Web Crypto: Bun, Node and browsers alike). */
export async function sha256Hex(
  bytes: ArrayBuffer | Uint8Array
): Promise<string> {
  // a copy in a plain ArrayBuffer: what every runtime's digest accepts
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes)
  const digest = await crypto.subtle.digest('SHA-256', view.slice().buffer)
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

/** A dropped link's name: the last part of its path, else its host. */
export function attachmentNameFor(url: string): string {
  try {
    const u = new URL(url)
    const last = decodeURIComponent(
      u.pathname.split('/').filter(Boolean).pop() ?? ''
    )
    return last || u.host
  } catch {
    return url
  }
}

/** What reads a stored file: the host's `/blob`, as `ServiceClient.getBlob` answers. */
export interface FileReader {
  getBlob(area: string, path: string): Promise<Blob | null>
}

/**
 * A stored file's bytes, checked against the sha256 its event names (board
 * #3560): the one way the CLI and the MCP server read an attachment. The
 * host answers "not found" both for a missing file and for one this
 * credential may not read, so the error names both.
 */
export async function downloadFile(
  reader: FileReader,
  file: AttachedFileRef
): Promise<Uint8Array> {
  const blob = await reader.getBlob(file.area, file.path)
  if (!blob)
    throw new Error(
      `${file.area}/${file.path}: the host answered "not found" — the file is gone, or this credential can't read ${file.area} (the host says the same for both; \`virta self login\` mints a token that can)`
    )
  const bytes = new Uint8Array(await blob.arrayBuffer())
  const sha = await sha256Hex(bytes)
  if (sha !== file.sha256)
    throw new Error(
      `${file.area}/${
        file.path
      }: the bytes don't match the sha256 the task names (${sha.slice(
        0,
        12
      )}… for ${file.sha256.slice(0, 12)}…)`
    )
  return bytes
}

/**
 * A file name to write an attachment under: its own name's last part, with
 * nothing that climbs out of the folder or hides the file. `taken` names
 * already written get a ` (2)`, ` (3)`… before the extension.
 */
export function downloadName(name: string, taken: ReadonlySet<string>): string {
  const base =
    (name.split(/[\\/]/).pop() ?? '')
      .replace(/[\u0000-\u001f<>:"|?*]/g, '_')
      .replace(/^\.+/, '')
      .trim() || 'attachment'
  if (!taken.has(base)) return base
  const dot = base.lastIndexOf('.')
  const [stem, ext] =
    dot > 0 ? [base.slice(0, dot), base.slice(dot)] : [base, '']
  for (let n = 2; ; n++) {
    const next = `${stem} (${n})${ext}`
    if (!taken.has(next)) return next
  }
}

/** What stores a file: the host's `/blob`, as `ServiceClient.putBlob` answers. */
export interface FileStore {
  putBlob(
    area: string,
    path: string,
    bytes: BodyInit,
    contentType: string
  ): Promise<{ path: string; bytes: number; sha256: string }>
}

/**
 * Check, hash and store one file (board #2436): the one path every surface
 * takes — the board's drop, `virta attach`, and the MCP tools — so the
 * rules can't drift apart. Refused before any bytes move.
 */
export async function uploadFile(
  store: FileStore,
  name: string,
  bytes: ArrayBuffer | Uint8Array,
  given = ''
): Promise<AttachedFileRef> {
  const size = bytes.byteLength
  const refusal = fileRefusal(name, size, given)
  if (refusal) throw new Error(refusal)
  const type = contentTypeFor(name, given)
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes)
  const sha256 = await sha256Hex(view)
  const stored = await store.putBlob(
    FILES_AREA_NAME,
    filePathFor(sha256, name),
    view.slice().buffer,
    type
  )
  return {
    area: FILES_AREA_NAME,
    path: stored.path,
    type,
    bytes: stored.bytes,
    sha256: stored.sha256,
  }
}

/** A stored file, as an attachment or a showcase names it. */
export interface AttachedFileRef {
  area: string
  path: string
  type: string
  bytes: number
  sha256: string
}

/** The largest icon kept inline in a project's settings (every replica carries it). */
export const ICON_MAX_BYTES = 48 * 1024

/**
 * A project icon from a file, as the inline image its settings keep (board
 * #2544): an SVG stays an SVG, sharp at any size; a PNG, JPEG, GIF or WebP
 * is kept as given. Anything else, or anything over ICON_MAX_BYTES, is
 * refused with the reason. The board shows it only as an <img>, where an
 * SVG's scripts never run.
 */
export function iconDataUrl(
  name: string,
  bytes: Uint8Array,
  given = ''
): { ok: true; url: string } | { ok: false; why: string } {
  const type = contentTypeFor(name, given)
  if (!/^image\/(svg\+xml|png|jpeg|gif|webp)$/.test(type))
    return {
      ok: false,
      why: `${name}: an icon is an SVG, PNG, JPEG, GIF or WebP`,
    }
  if (bytes.byteLength > ICON_MAX_BYTES)
    return {
      ok: false,
      why: `${name} is ${Math.ceil(
        bytes.byteLength / 1024
      )} KB; an icon can be at most ${ICON_MAX_BYTES / 1024} KB${
        type === 'image/svg+xml'
          ? ' (an optimiser such as svgo usually shrinks an SVG a lot)'
          : ' (a 128px square is plenty)'
      }`,
    }
  let binary = ''
  for (let i = 0; i < bytes.length; i += 0x8000)
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000))
  return { ok: true, url: `data:${type};base64,${btoa(binary)}` }
}

/** Bytes from base64 — how MCP carries a file (JSON has no bytes). */
export function bytesFromBase64(text: string): Uint8Array {
  const raw = atob(text.replace(/^data:[^,]*,/, '').replace(/\s+/g, ''))
  const out = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i)
  return out
}
